import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import {
  adminCompaniesApi,
  type AdminCompanyListItem,
  type AdminCompanyListParams,
  type AdminCompanyListSortBy,
  type AdminCompanyListSortOrder,
  type AdminCompanyVerificationStatus,
} from '../lib/api';
import { Badge } from '../components/Badge';
import MergeBacklogSection from '../components/MergeBacklogSection';
import TenantMergeRequestSection from '../components/TenantMergeRequestSection';
import { VerificationBadge, VERIFICATION_STATUS_MAP } from '../components/VerificationBadge';

/** 가입(구독) 유무 필터 — 정본 클레임(claimedAt) 축과 다르다 */
type SubscriptionFilter = '' | 'true' | 'false';
type StatusFilter = '' | AdminCompanyVerificationStatus;

const STATUS_SORT_ORDER: Record<AdminCompanyVerificationStatus, number> = {
  FULLY_VERIFIED: 0,
  STATUS_VERIFIED: 1,
  NOT_APPLICABLE: 2,
};

// registrationNumber가 없는 Company는 개별(단독) 행으로 표시되는 "solo" 그룹을 가진다.
type Group = {
  key: string;
  registrationNumber: string | null;
  items: AdminCompanyListItem[];
  hasFully: boolean;
};

function groupItems(items: AdminCompanyListItem[]): Group[] {
  const groupsByKey = new Map<string, Group>();
  const soloGroups: Group[] = [];

  for (const item of items) {
    if (!item.registrationNumber) {
      soloGroups.push({
        key: `solo:${item.id}`,
        registrationNumber: null,
        items: [item],
        hasFully: item.verificationStatus === 'FULLY_VERIFIED',
      });
      continue;
    }

    const existing = groupsByKey.get(item.registrationNumber);
    if (existing) {
      existing.items.push(item);
      if (item.verificationStatus === 'FULLY_VERIFIED') existing.hasFully = true;
    } else {
      groupsByKey.set(item.registrationNumber, {
        key: `reg:${item.registrationNumber}`,
        registrationNumber: item.registrationNumber,
        items: [item],
        hasFully: item.verificationStatus === 'FULLY_VERIFIED',
      });
    }
  }

  const grouped = [...groupsByKey.values(), ...soloGroups];
  for (const g of grouped) {
    g.items.sort(
      (a, b) =>
        STATUS_SORT_ORDER[a.verificationStatus] - STATUS_SORT_ORDER[b.verificationStatus],
    );
  }
  return grouped;
}

export default function Companies() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<StatusFilter>('');
  const [hasSubscription, setHasSubscription] = useState<SubscriptionFilter>('');
  const [sortBy, setSortBy] = useState<AdminCompanyListSortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<AdminCompanyListSortOrder>('desc');
  const limit = 20;

  const params: AdminCompanyListParams = {
    page,
    limit,
    sortBy,
    sortOrder,
  };
  if (q) params.q = q;
  if (verificationStatus) params.verificationStatus = verificationStatus;
  if (hasSubscription) params.hasSubscription = hasSubscription;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies', params],
    queryFn: () => adminCompaniesApi.list(params),
    placeholderData: (prev) => prev,
  });

  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const groups = useMemo(() => groupItems(items), [items]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setQ(qInput.trim());
  };

  const handleReset = () => {
    setQInput('');
    setQ('');
    setVerificationStatus('');
    setHasSubscription('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">사업장 관리</h2>
        <div className="text-sm text-gray-500">
          총 {total.toLocaleString('ko-KR')}개
        </div>
      </div>

      {/* 병합 보류 큐 — 보류 건이 없으면 스스로 사라진다 */}
      <MergeBacklogSection />

      {/* 조직 통합 중재 큐 — 위 보류 큐(자동 흡수가 건너뛴 건)와 달리 동의 흐름이 멈춘 건이다 */}
      <TenantMergeRequestSection />

      {/* 필터/검색 */}
      <Card radius="lg" className="mb-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              검색 (상호 / 사업자번호)
            </label>
            <input
              type="text"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="예: 홍길동물산 또는 1234567890"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">검증 상태</label>
            <select
              value={verificationStatus}
              onChange={(e) => {
                setVerificationStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">전체</option>
              <option value="FULLY_VERIFIED">{VERIFICATION_STATUS_MAP.FULLY_VERIFIED.label}</option>
              <option value="STATUS_VERIFIED">{VERIFICATION_STATUS_MAP.STATUS_VERIFIED.label}</option>
              <option value="NOT_APPLICABLE">{VERIFICATION_STATUS_MAP.NOT_APPLICABLE.label}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">조직 연결</label>
            <select
              value={hasSubscription}
              onChange={(e) => {
                setHasSubscription(e.target.value as SubscriptionFilter);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">전체</option>
              <option value="true">연결됨</option>
              <option value="false">연결 안됨</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">정렬</label>
            <div className="flex gap-1">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as AdminCompanyListSortBy)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="createdAt">생성일</option>
                <option value="name">상호</option>
                <option value="ntsVerifiedAt">NTS 검증일</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as AdminCompanyListSortOrder)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="desc">내림차순</option>
                <option value="asc">오름차순</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" variant="primary" size="sm">
              검색
            </Button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200"
            >
              초기화
            </button>
          </div>
        </form>
      </Card>

      {/* 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        {isLoading && !data ? (
          <div className="px-4 py-12 text-center text-gray-500">로딩 중...</div>
        ) : error ? (
          <div className="px-4 py-12 text-center text-red-600">
            데이터를 불러오는데 실패했습니다.
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            조회된 사업장이 없습니다.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상호
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      대표자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      검증 상태
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      생성 조직
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      본사 연결
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      현장
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      생성일
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groups.map((group) => {
                    const isGrouped = !!group.registrationNumber && group.items.length > 1;
                    return (
                      <Fragment key={group.key}>
                        {isGrouped && (
                          <tr className="bg-gray-50">
                            <td
                              colSpan={7}
                              className="px-4 py-2 text-xs text-gray-700 font-medium"
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-500">사업자번호</span>
                                <span className="font-mono text-gray-900">
                                  {group.registrationNumber}
                                </span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-600">
                                  {group.items.length}개 레코드
                                </span>
                                {group.hasFully ? (
                                  <Badge color="green" label="FULLY 보유" />
                                ) : (
                                  <Badge color="gray" label="FULLY 없음" />
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        {group.items.map((c) => (
                          <tr
                            key={c.id}
                            className={`cursor-pointer hover:bg-gray-50 ${
                              isGrouped ? 'border-l-4 border-gray-200' : ''
                            }`}
                            onClick={() => navigate(`/companies/${c.id}`)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{c.name}</div>
                              {!isGrouped && c.registrationNumber && (
                                <div className="text-xs font-mono text-gray-500">
                                  {c.registrationNumber}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {c.ceoName ?? '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <VerificationBadge status={c.verificationStatus} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {c.createdByOrgName ?? (c.createdByOrgId ? c.createdByOrgId : '-')}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {c.hasSubscription ? (
                                <Badge color="blue" label="연결됨" />
                              ) : (
                                <Badge color="gray" label="-" />
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {c.siteCount}개
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {new Date(c.createdAt).toLocaleDateString('ko-KR')}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <span className="text-sm text-gray-600">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
